// Cart line model of the aldoo.com storefront on top of the framework cart
// (aldoo-ra/Store, config.multiItem). A line is the framework item shape —
// { pid, product_id, name, thumbnail, amount, currency, quantity, options }
// — plus `product`: the catalog fields the cart, the checkout summary and the
// order payload need. `options` is the extensible per-line data:
//   { platform: [{ platformId, quantity }] | [] (license distribution),
//     licenses: <total licenses>, seller: { name }, universal: <bool> }
// What the buyer chose on the product page (the store product extensions)
// rides beside it: { sku, choices, choiceLabels, components, lineKey }, and
// `lineAmount` — the unit price in cents before the discount — when an
// extension priced the selection (aldoo-ra/Store/lines).
import { normalizeProduct, parseRealPrice, isPhysicalProduct, isLicenseProduct } from "../utils/productUtils"
import { activeDiscountPercent, applyDiscount } from "../utils/discountUtils"
import { presaleOf } from "../utils/presale"
import { lineKey as storeLineKey, withSelection, selectionOf } from "aldoo-ra/Store/lines"

// Catalog fields kept on the persisted line (the cart lives in localStorage)
const LINE_PRODUCT_FIELDS = [
  "pid", "name", "square", "thumb", "author", "author_display_name", "publisher", "real_price", "real_money_cost", "real_money_currency",
  "real_money_cost_base", "real_money_currency_base", "discounts", "bookSettings", "profile", "contentTypeData", "isPhysicalProduct",
  "deliveryDays", "free_shipping", "stock", "availableForPurchase", "allowCreditsPurchase", "weight", "free_access",
  "presale", "releaseDate", "maxPerOrder", "orderContact",
  // the catalog categories: analytics names an item's category from them
  "categories",
]

// a licence: its profile says so (contentTypeData — a line kept from before profiles)
export const isLicenseKeyProduct = (product) => isLicenseProduct(product) || !!product?.contentTypeData?.isLicenseKey

export const productPlatforms = (product) => product?.bookSettings?.platforms || []

/** License products offer their keys per supported platform */
export const hasPlatformSelection = (product) => isLicenseKeyProduct(product) && productPlatforms(product).length > 0

export const platformId = (entry) => entry?.platform?._id || entry?.platform || null

/** Unit price in cents after the active discount */
export function itemUnitPrice(product) {
  const parsed = parseRealPrice(product?.real_price)
  const base = parsed?.amount ?? (parseInt(product?.real_money_cost) || 0)
  return applyDiscount(base, activeDiscountPercent(product))
}

// the currency the product is priced in (its own; none when it has no money price)
export const productCurrency = (product) => parseRealPrice(product?.real_price)?.currency || product?.real_money_currency || null

export const lineKey = storeLineKey

export const lineProduct = (line) => line.product || line

/**
 * Unit price of a cart line in cents after the active discount: the price an
 * extension gave the selection when it gave one, the product's otherwise.
 */
export function lineUnitPrice(line) {
  if (!Number.isFinite(line?.lineAmount)) return itemUnitPrice(lineProduct(line))
  return applyDiscount(line.lineAmount, activeDiscountPercent(lineProduct(line)))
}

/** A line's unit price before its discount (cents, in the product's currency) — the struck price beside the paid one. */
export function lineUnitPriceBefore(line) {
  if (Number.isFinite(line?.lineAmount)) return line.lineAmount
  const product = lineProduct(line)
  return parseRealPrice(product?.real_price)?.amount ?? (parseInt(product?.real_money_cost) || 0)
}

const lineSeller = (line) => line.options?.seller?.name || lineProduct(line).publisher || null

/**
 * Cart line for a catalog product. `selection` is the product page's
 * selection line ({ sku, choices, choiceLabels, components, amount }); its
 * `amount` (cents, before the discount) prices the line when `priced` is set.
 */
export function toCartItem(product, quantity = 1, options = {}, selection = null) {
  const normalized = normalizeProduct(product)
  const trimmed = {}
  for (const key of LINE_PRODUCT_FIELDS) if (normalized[key] !== undefined) trimmed[key] = normalized[key]
  const priced = selection?.priced && Number.isFinite(selection.amount)
  const unit = priced ? applyDiscount(selection.amount, activeDiscountPercent(normalized)) : itemUnitPrice(normalized)
  const seller = normalized.publisher ? { name: normalized.publisher } : undefined
  const line = {
    pid: normalized.pid,
    product_id: normalized.pid,
    name: normalized.name,
    thumbnail: typeof normalized.square === "string" ? normalized.square : null,
    amount: unit / 100,
    currency: productCurrency(normalized),
    quantity,
    options: { ...(seller ? { seller } : {}), ...options },
    product: trimmed,
    ...(priced ? { lineAmount: selection.amount } : {}),
  }
  return withSelection(line, selection)
}

export const isDigitalOnly = (lines) => lines.every((line) => !isPhysicalProduct(lineProduct(line)) && !isLicenseKeyProduct(lineProduct(line)))

export const hasPhysicalItems = (lines) => lines.some((line) => isPhysicalProduct(lineProduct(line)))

/** License lines that still need their platform distribution */
export const linesNeedingPlatform = (lines) =>
  lines.filter((line) => hasPlatformSelection(lineProduct(line)) && !Array.isArray(line.options?.platform))

export const totalLicenses = (line) =>
  Array.isArray(line.options?.platform) && line.options.platform.length
    ? line.options.platform.reduce((sum, entry) => sum + (entry.quantity || 0), 0)
    : line.quantity || 1

/** Lines grouped by seller (merchant), keeping the cart order */
export function groupBySeller(lines) {
  const groups = []
  for (const line of lines) {
    const name = lineSeller(line)
    let group = groups.find((g) => g.name === name)
    if (!group) {
      group = { name, lines: [], subtotal: 0 }
      groups.push(group)
    }
    group.lines.push(line)
    group.subtotal += lineUnitPrice(line) * (line.quantity || 1)
  }
  return groups
}

/**
 * What the lines come to. `convert(amount, currency)` puts each line into the
 * cart's currency; without it every line is taken as it is priced, which only
 * holds when the basket is of one currency.
 */
export const cartSubtotal = (lines, convert) =>
  lines.reduce((sum, line) => {
    const product = lineProduct(line)
    const amount = lineUnitPrice(line) * (line.quantity || 1)
    return sum + (convert ? convert(amount, productCurrency(product)) : amount)
  }, 0)

/** Cart lines the way store {checkout_payment} / {calculate_shipping} expect them */
export const toCheckoutCart = (lines) =>
  lines.map((line) => ({
    product: { pid: line.pid },
    pid: line.pid,
    quantity: line.quantity || 1,
    platformDistribution: line.options?.platform?.length ? line.options.platform : undefined,
    // the buyer's selection (sku, choices, choiceLabels, components, lineKey);
    // nothing for a plain line, so its payload is what it always was
    ...selectionOf(line),
  }))

/** The pre-sale lines of the cart, each with its promise: [{ line, name, presale: { releaseDate, deliverBy } }] */
export const presaleLines = (lines) =>
  lines.flatMap((line) => {
    const presale = presaleOf(lineProduct(line))
    return presale ? [{ line, name: line.name || lineProduct(line).name, presale }] : []
  })

/** Longest expected delivery of the physical lines, in days (null when unknown) */
export const maxDeliveryDays = (lines) =>
  lines.reduce((max, line) => {
    const product = lineProduct(line)
    if (!isPhysicalProduct(product)) return max
    const days = product.deliveryDays ?? 3
    return max == null || days > max ? days : max
  }, null)

// GA4 item object for tracking events
export const gaItem = (product, quantity = 1) => ({
  item_id: product.pid,
  item_name: product.name,
  price: itemUnitPrice(normalizeProduct(product)) / 100,
  quantity,
})
